import React, { useEffect } from "react";
import { useRecoilState } from "recoil";
import { add } from "date-fns";
import { useForm } from "react-hook-form";
import { produce } from "immer";

import { MIN_DATES, MAX_DATES } from "config";
import t from "lib/i18n";
import { countRailcards } from "lib/utils";
import { getRoversSearchURL } from "lib/searchUrl";
import useConfig from "hooks/useConfig";
import { roversFormState } from "state";
import useLayout from "hooks/useLayout";
import DateTime from "models/DateTime";
import "styles/pages/other.scss";
import useGTM, { useGTMChange } from "hooks/useGTM";
import * as tracking from "lib/tracking";

import FeatureFlag from "components/helpers/FeatureFlag";
import Button, { ButtonTypes } from "components/ui/Button";
import NewDatesSelector from "components/datetime/DatesSelector";
import Passengers from "components/Passengers";
import Select from "components/ui/Select";
import SrOnly from "components/helpers/SrOnly";

const Rovers = () => {
  const {
    defaultValues,
    redirectUrl,
    searchCallBack,
    mode,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
  } = useConfig();

  const [form, setForm] = useRecoilState(roversFormState); // retrieve the form's state from a global state

  const configRoverType = defaultValues?.ticketTypes?.rovers?.roverType;
  const configAdults = defaultValues?.ticketTypes?.rovers?.adults;
  const configChildren = defaultValues?.ticketTypes?.rovers?.children;
  const configFromDateTime = defaultValues?.ticketTypes?.rovers?.fromDateTime;
  const configRailCards = defaultValues?.ticketTypes?.rovers?.railcards;

  const methods = useForm({
    defaultValues: form ?? {
      roverType: configRoverType || "14R",
      adults: configAdults ?? 1,
      children: configChildren ?? 0,
      fromDateTime: configFromDateTime
        ? new DateTime(configFromDateTime)
        : new DateTime().addDays(3),
      railcards: configRailCards || [],
      ticketType: "rovers",
      toDateTime: null
    }
  });
  const { clearErrors, errors, handleSubmit, getValues, register, setValue, watch } = methods;
  const { isMobile } = useLayout(true);

  const { adults, children, fromDateTime, railcards, ticketType, toDateTime, roverType } = watch([
    "adults",
    "children",
    "fromDateTime",
    "railcards",
    "ticketType",
    "toDateTime",
    "roverType"
  ]);

  useGTMChange(roverType, tracking.trackRoversType);
  useGTMChange(fromDateTime, tracking.trackRoversStartDate);
  useGTMChange(adults, tracking.trackRoversAdults);
  useGTMChange(children, tracking.trackRoversChildren);
  useGTMChange(railcards, tracking.trackRoversRailcards);

  useEffect(() => {
    register({ name: "adults" }, { required: true });
    register({ name: "children" }, { required: true });
    register(
      { name: "railcards" },
      {
        required: false,
        validate: {
          maximumRailcards: value => {
            return (
              countRailcards(value) <= getValues("adults") + getValues("children") ||
              "Too many railcards."
            );
          }
        }
      }
    );
    register({ name: "ticketType" }, { required: true });
    register({ name: "toDateTime" }, { required: false });
    register({ name: "fromDateTime" }, { required: true });
    register({ name: "roverType" }, { required: true });

    return () => {
      // Save the form state
      const nextForm = produce(form, () => getValues);

      setForm(nextForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGTM = useGTM();
  const onSubmit = data => {
    const urlSearch = getRoversSearchURL({
      data,
      redirectUrl,
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    });
    dispatchGTM(tracking.search());

    if (searchCallBack) {
      searchCallBack(urlSearch, data);
    } else {
      window.top.location.href = urlSearch;
    }
  };

  const setFieldValue = field => value => {
    setValue(field, value, true);

    if (field === "ticketType") {
      clearErrors("toDateTime");
    }
  };

  const onChangeFromDateTime = newValue => {
    setValue("fromDateTime", fromDateTime?.produce(newValue), true);
  };

  const onChangeToDateTime = newValue => {
    setValue("toDateTime", toDateTime?.produce(newValue), true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="otrl-jp__other__control-bar">
        <div className="otrl-jp__other__control-bar__stations">
          <SrOnly>
            <label for="rover-type-select">{t("tickets.rover.select.aria")}</label>
          </SrOnly>
          <Select
            onChange={setFieldValue("roverType")}
            value={roverType}
            className="otrl-jp__other__control-bar__stations__select"
            id="rover-type-select"
          >
            <option value="14R">All Line Rover (14 days)</option>
            <option value="R07">All Line Rover (7 days)</option>
            <FeatureFlag name="enableKentRover">
              <option value="MTR">Kent Rover</option>
            </FeatureFlag>
            <FeatureFlag name="enableTransPennineRover">
                <option value="E37">East Midlands Rover (3 days in 7)</option>
                <option value="EM7">East Midlands Rover (7 days)</option>
                <option value="NC8">North Country Rover (4 days in 8)</option>
                <option value="NE8">North East Rover (4 days in 8)</option>
                <option value="NE7">North East Rover (7 days)</option>
                <option value="NES">North of England City Explorer (4 days in 8)</option>
                <option value="N37">North West Rover (4 days in 8)</option>
                <option value="NW7">North West Rover (7 days)</option>
                <option value="WER">Northern Only Weekend Rover</option>
            </FeatureFlag>
          </Select>
        </div>
        {isMobile ? (
          <div className="otrl-jp__other__journey-type-and-dates">
            <NewDatesSelector
              errors={errors}
              fromDateTime={fromDateTime}
              onChangeFromDateTime={onChangeFromDateTime}
              onChangeToDateTime={onChangeToDateTime}
              ticketType={ticketType}
              toDateTime={toDateTime}
              minDate={add(new Date(), MIN_DATES.ROVERS)}
              maxDate={add(new Date(), MAX_DATES.ROVERS)}
              isOther
            />
          </div>
        ) : (
          <NewDatesSelector
            errors={errors}
            fromDateTime={fromDateTime}
            onChangeFromDateTime={onChangeFromDateTime}
            onChangeToDateTime={onChangeToDateTime}
            ticketType={ticketType}
            toDateTime={toDateTime}
            minDate={add(new Date(), MIN_DATES.ROVERS)}
            maxDate={add(new Date(), MAX_DATES.ROVERS)}
            isOther
          />
        )}
        <Passengers
          adults={adults}
          children={children}
          railcards={railcards}
          onChange={setFieldValue}
          errorRailcards={errors["railcards"]}
        />
        <Button
          className="otrl-jp__other__submit"
          text={t(isMobile ? "tickets.submit.long" : "tickets.submit")}
          type={ButtonTypes.SUBMIT}
        />
      </div>
    </form>
  );
};

export default Rovers;
